import { useEffect, useMemo, useRef, useState } from 'react';
import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import {
    Currency,
    DemeterCommodity,
    DemeterSymbolContractModel,
    DemeterSymbolModel,
    DemeterUserStoreType,
    ExchangeType,
    SymbolCategory,
    UnitOfMeasure,
} from '../../../../Generated/Raven-Demeter';
import useSymbolsApi from '../../../Apis/Hooks/useSymbolsApiHook';
import useUserStoreApi from '../../../Apis/Hooks/useUserStoreApiHook';
import ButtonWithTooltip from '../../../Components/Form/Buttons/ButtonWithTooltip/ButtonWithTooltip';
import Dropdown from '../../../Components/Form/Inputs/Dropdown';
import TutorialLink from '../../../Components/Form/Links/TutorialLink/TutorialLink';
import ComponentHeader from '../../../Components/Headers/ComponentHeader';
import ComponentSubHeader from '../../../Components/Headers/ComponentSubHeader';
import TableSkeleton from '../../../Components/Layout/TableSkeleton';
import CalendarSpreadCalculatorModal from '../../../Components/Modals/ProductsModal/CalendarSpreadCalculatorModal';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './CalendarSpreadCalculator.module.scss';
import CalendarSpreadCalculatorChartCarousel from './CalendarSpreadCalculatorChartCarousel';
import CalendarSpreadCalculatorGrid from './CalendarSpreadCalculatorGrid';
import calendarSpreadCalculatorService from './CalendarSpreadCalculatorService';
import {
    dairySpreadUnitOfMeasures,
    energySpreadUnitOfMeasures,
    ICalendarSpreadCalculationResult,
    ICalendarSpreadSelections,
    spreadCurrencies,
} from './CalendarSpreadDefinitions';

const defaultSpreadCurrency = Currency.Eur;
const defaultSpreadUnitOfMeasure = UnitOfMeasure.MetricTon;

const CalendarSpreadCalculator = () => {
    const [translations] = useLanguage();
    const [productSelectorOpen, setProductSelectorOpen] = useState(false);
    const [calendarSpreadSelections, setCalendarSpreadSelections] = useState<ICalendarSpreadSelections>();
    const [calculationResult, setCalculationResult] = useState<ICalendarSpreadCalculationResult>();
    const [isInitialized, setIsInitialized] = useState(false);
    const symbols = useSymbolsApi(true);
    const [calculatorSelectionsResponse, updateCalculatorSelections] = useUserStoreApi(DemeterUserStoreType.CalculatorSelections);
    const hasLoadedFromUserStore = useRef(false);

    const symbolOptions = useMemo(() => {
        if (!symbols) {
            return [];
        }

        return symbols.filter(
            (symbol) =>
                symbol.symbolCategory !== SymbolCategory.Currency &&
                symbol.symbolContracts.length >= 1 &&
                symbol.commodity !== DemeterCommodity.CarbonCredit &&
                symbol.commodity !== DemeterCommodity.DieselTax &&
                symbol.exchange !== ExchangeType.Mdx &&
                symbol.exchange !== ExchangeType.Dce,
        );
    }, [symbols]);

    const getSymbol2Contracts = (symbol2Constracts: DemeterSymbolContractModel[], symbol1Contract: DemeterSymbolContractModel) =>
        symbol2Constracts.filter(
            (contract) =>
                (contract.year === symbol1Contract.year &&
                    symbol1Contract.reutersInstrumentCode !== contract.reutersInstrumentCode &&
                    contract.month >= symbol1Contract.month) ||
                contract.year > symbol1Contract.year,
        );

    const buildSelectionsFromStored = (storedCalendarSpread: ICalendarSpreadSelections): ICalendarSpreadSelections | null => {
        const storedSymbol1 = symbolOptions.find((s) => s.reutersInstrumentCodePrefix === storedCalendarSpread.symbol1?.reutersInstrumentCodePrefix);
        const storedSymbol2 = symbolOptions.find((s) => s.reutersInstrumentCodePrefix === storedCalendarSpread.symbol2?.reutersInstrumentCodePrefix);

        if (!storedSymbol1 || !storedSymbol2) {
            return null;
        }

        const contract1 =
            storedSymbol1.symbolContracts.find((c) => c.reutersInstrumentCode === storedCalendarSpread.contract1?.reutersInstrumentCode) ||
            storedSymbol1.symbolContracts[0];

        const contracts2 = getSymbol2Contracts(storedSymbol2.symbolContracts, contract1);
        const contract2 = contracts2.find((c) => c.reutersInstrumentCode === storedCalendarSpread.contract2?.reutersInstrumentCode) || contracts2[0];

        return {
            symbol1: storedSymbol1,
            contracts1: storedSymbol1.symbolContracts,
            contract1,
            symbol2: storedSymbol2,
            contracts2,
            contract2,
            currency: storedCalendarSpread.currency,
            unitOfMeasure: storedCalendarSpread.unitOfMeasure,
        };
    };

    const handleProductSelectionChange = (symbol1: DemeterSymbolModel, symbol2: DemeterSymbolModel) => {
        setCalendarSpreadSelections((previous) => {
            if (!previous) {
                return previous;
            }

            const symbolContract1 = symbol1.symbolContracts[0];
            const symbol2Contracts = getSymbol2Contracts(symbol2.symbolContracts, symbolContract1);

            const newSelections = {
                symbol1,
                contracts1: symbol1.symbolContracts,
                contract1: symbol1.symbolContracts[0],
                symbol2,
                contracts2: symbol2Contracts,
                contract2: symbol2Contracts.length ? symbol2Contracts[0] : symbol2.symbolContracts[0],
                currency: symbol1.currency ?? Currency.Eur,
                unitOfMeasure: calendarSpreadCalculatorService.getUnitOfMeasure(symbol1, previous.unitOfMeasure ?? defaultSpreadUnitOfMeasure),
            };

            saveSelections(newSelections);
            return newSelections;
        });
    };

    useEffect(() => {
        if (!symbolOptions || !symbolOptions.length || isInitialized) {
            return;
        }

        if (calculatorSelectionsResponse === undefined) {
            return;
        }

        const storedSpreadCalculatorSelections = calculatorSelectionsResponse?.userStore?.value?.spreadCalculator;
        let initialSelections: ICalendarSpreadSelections;

        if (storedSpreadCalculatorSelections) {
            const selections = buildSelectionsFromStored(storedSpreadCalculatorSelections as ICalendarSpreadSelections);
            if (selections) {
                initialSelections = selections;
            }
        }

        if (!initialSelections!) {
            const dairySymbols = symbolOptions.filter(
                (x) => x.symbolCategory === SymbolCategory.Dairy && x.exchange === ExchangeType.Eex && x.symbolContracts.length >= 1,
            );
            const symbol1 = dairySymbols.length ? dairySymbols[0] : symbolOptions[0];
            const symbol2 = dairySymbols.length ? dairySymbols[0] : symbolOptions[0];

            initialSelections = {
                symbol1,
                contracts1: symbol1.symbolContracts,
                contract1: symbol1.symbolContracts[0],
                symbol2,
                contracts2: symbol2.symbolContracts,
                contract2: symbol2.symbolContracts[1],
                currency: defaultSpreadCurrency,
                unitOfMeasure: calendarSpreadCalculatorService.getUnitOfMeasure(symbol1, defaultSpreadUnitOfMeasure),
            };
        }

        setCalendarSpreadSelections(initialSelections);
        setIsInitialized(true);
        hasLoadedFromUserStore.current = true;
    }, [symbolOptions, calculatorSelectionsResponse, isInitialized]);

    useEffect(() => {
        if (!calculatorSelectionsResponse || !symbolOptions || !symbolOptions.length || !isInitialized) {
            return;
        }

        const storedSpreadCalculatorSelections = calculatorSelectionsResponse.userStore?.value?.spreadCalculator;
        if (!storedSpreadCalculatorSelections) {
            return;
        }

        const updatedSelections = buildSelectionsFromStored(storedSpreadCalculatorSelections as ICalendarSpreadSelections);
        if (!updatedSelections) {
            return;
        }

        // Only update if selections have changed
        if (
            !calendarSpreadSelections ||
            calendarSpreadSelections.symbol1?.reutersInstrumentCodePrefix !== updatedSelections.symbol1?.reutersInstrumentCodePrefix ||
            calendarSpreadSelections.symbol2?.reutersInstrumentCodePrefix !== updatedSelections.symbol2?.reutersInstrumentCodePrefix ||
            calendarSpreadSelections.contract1?.reutersInstrumentCode !== updatedSelections.contract1?.reutersInstrumentCode ||
            calendarSpreadSelections.contract2?.reutersInstrumentCode !== updatedSelections.contract2?.reutersInstrumentCode ||
            calendarSpreadSelections.currency !== updatedSelections.currency ||
            calendarSpreadSelections.unitOfMeasure !== updatedSelections.unitOfMeasure
        ) {
            setCalendarSpreadSelections(updatedSelections);
        }
    }, [calculatorSelectionsResponse, symbolOptions, isInitialized]);

    const saveSelections = (selections: ICalendarSpreadSelections) => {
        if (hasLoadedFromUserStore.current) {
            const currentSelections = calculatorSelectionsResponse?.userStore?.value || {};
            updateCalculatorSelections({
                ...currentSelections,
                spreadCalculator: selections,
            });
        }
    };

    const currencyDropdownOptions = useMemo(
        () =>
            spreadCurrencies.map((x) => ({
                label: x,
                value: x,
            })),
        [translations],
    );

    const unitOfMeasureDropdownOptions = useMemo(() => {
        if (!calendarSpreadSelections) {
            return [];
        }

        const validUnitsOfMeasure =
            calendarSpreadSelections.symbol1.symbolCategory === SymbolCategory.Energy ? energySpreadUnitOfMeasures : dairySpreadUnitOfMeasures;

        return validUnitsOfMeasure.map((x) => ({
            label: translations.unitOfMeasure[x],
            value: x,
        }));
    }, [translations, calendarSpreadSelections]);

    const isLoading = !calendarSpreadSelections || !isInitialized;

    return (
        <>
            <div className={styles.calendar_spread_calculator_header_row}>
                <ComponentHeader
                    title={translations.calculators.text.calendarSpreadCalculator}
                    rightSideComponent={<TutorialLink link={applicationConstants.TutorialLinks.forwardSpreadCalculator} />}
                />
            </div>
            {isLoading ? (
                <TableSkeleton />
            ) : (
                <>
                    <div className={styles.calendar_spread_calculator_content_row}>
                        <div data-testid="CalendarSpreadCalculator" className={styles.calendar_spread_calculator_form}>
                            <div className={styles.calendar_spread_calculator_container}>
                                <div className={styles.calendar_spread_calculator_row}>
                                    <div className={styles.calendar_spread_calculator_cell}>
                                        <ComponentSubHeader title={`${translations.words.product} 1`} />
                                        <ButtonWithTooltip
                                            text={formattingService.toDisplayName(calendarSpreadSelections.symbol1) ?? ''}
                                            maximumLength={25}
                                            onClick={() => setProductSelectorOpen(true)}
                                            track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'OpenProduct1SelectorClicked' }}
                                        />
                                    </div>
                                    <div className={styles.calendar_spread_calculator_cell}>
                                        <ComponentSubHeader title={`${translations.words.product} 2`} />
                                        <ButtonWithTooltip
                                            text={formattingService.toDisplayName(calendarSpreadSelections.symbol2) ?? ''}
                                            maximumLength={25}
                                            onClick={() => setProductSelectorOpen(true)}
                                            track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'OpenProduct2SelectorClicked' }}
                                        />
                                    </div>
                                </div>
                                <div className={styles.calendar_spread_calculator_row}>
                                    <div className={styles.calendar_spread_calculator_cell}>
                                        <ComponentSubHeader title={`${translations.words.contract} 1`} />
                                        <Dropdown
                                            value={calendarSpreadSelections?.contract1 ?? null}
                                            options={
                                                calendarSpreadSelections?.contracts1.map((x) => ({
                                                    label: x.monthYearName,
                                                    value: x,
                                                })) ?? []
                                            }
                                            handleOptionChange={(value) => {
                                                setCalendarSpreadSelections((previous) => {
                                                    if (!previous || !value) {
                                                        return previous;
                                                    }

                                                    const newContracts2 = getSymbol2Contracts(previous.symbol2.symbolContracts, value);
                                                    let newSelections: ICalendarSpreadSelections;
                                                    if (
                                                        (value.month >= previous.contract2.month && value.year === previous.contract2.year) ||
                                                        value.year > previous.contract2.year
                                                    ) {
                                                        newSelections = {
                                                            ...previous,
                                                            contract1: value,
                                                            contract2: !newContracts2.length
                                                                ? previous.contracts2[previous.contracts2.length - 1]
                                                                : newContracts2[0],
                                                            contracts2: !newContracts2.length
                                                                ? [previous.contracts2[previous.contracts2.length - 1]]
                                                                : newContracts2,
                                                        };
                                                    } else {
                                                        newSelections = {
                                                            ...previous,
                                                            contract1: value,
                                                            contracts2: newContracts2,
                                                        };
                                                    }

                                                    saveSelections(newSelections);
                                                    return newSelections;
                                                });
                                            }}
                                            track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'Contract1Changed' }}
                                        />
                                    </div>
                                    <div className={styles.calendar_spread_calculator_cell}>
                                        <ComponentSubHeader title={`${translations.words.contract} 2`} />
                                        <Dropdown
                                            value={calendarSpreadSelections?.contract2 ?? null}
                                            options={
                                                getSymbol2Contracts(calendarSpreadSelections.symbol2.symbolContracts, calendarSpreadSelections.contract1).map(
                                                    (x) => ({
                                                        label: x.monthYearName,
                                                        value: x,
                                                    }),
                                                ) ?? []
                                            }
                                            handleOptionChange={(value) => {
                                                if (!value || !calendarSpreadSelections) {
                                                    return;
                                                }

                                                const newSelections = {
                                                    ...calendarSpreadSelections,
                                                    contract2: value,
                                                };
                                                setCalendarSpreadSelections(newSelections);
                                                saveSelections(newSelections);
                                            }}
                                            track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'Contract2Changed' }}
                                        />
                                    </div>
                                </div>
                                <div className={styles.calendar_spread_calculator_row}>
                                    <div className={styles.calendar_spread_calculator_cell}>
                                        <ComponentSubHeader title={translations.words.currency} />
                                        <Dropdown
                                            value={calendarSpreadSelections?.currency}
                                            options={currencyDropdownOptions}
                                            handleOptionChange={(value) => {
                                                if (calendarSpreadSelections) {
                                                    const newSelections = {
                                                        ...calendarSpreadSelections,
                                                        currency: value,
                                                    };
                                                    setCalendarSpreadSelections(newSelections);
                                                    saveSelections(newSelections);
                                                }
                                            }}
                                            track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'CurrencyChanged' }}
                                        />
                                    </div>
                                    <div className={styles.calendar_spread_calculator_cell}>
                                        <ComponentSubHeader title={translations.words.unit} />
                                        <Dropdown
                                            value={calendarSpreadSelections?.unitOfMeasure}
                                            options={unitOfMeasureDropdownOptions}
                                            handleOptionChange={(value) => {
                                                if (calendarSpreadSelections) {
                                                    const newSelections = {
                                                        ...calendarSpreadSelections,
                                                        unitOfMeasure: value,
                                                    };
                                                    setCalendarSpreadSelections(newSelections);
                                                    saveSelections(newSelections);
                                                }
                                            }}
                                            track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'UnitOfMeasureChanged' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.calendar_spread_calculator_content_right}>
                            <CalendarSpreadCalculatorChartCarousel
                                calendarSpreadCalculatorSelections={calendarSpreadSelections}
                                currency={calendarSpreadSelections?.currency}
                                unitOfMeasure={calendarSpreadSelections?.unitOfMeasure}
                                onCalculationCalculated={setCalculationResult}
                            />

                            <CalendarSpreadCalculatorGrid calculationResult={calculationResult} />
                        </div>
                    </div>
                    {symbolOptions && (
                        <CalendarSpreadCalculatorModal
                            productOptions={symbolOptions}
                            open={productSelectorOpen}
                            symbol1={calendarSpreadSelections.symbol1}
                            symbol2={calendarSpreadSelections.symbol2}
                            handleChange={handleProductSelectionChange}
                            handleClose={() => setProductSelectorOpen(false)}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default CalendarSpreadCalculator;
